import { useState, useCallback } from 'react';
import { QRCodeService, PDFService, FileService } from '../services/qrcode';
import { Trip, TripParticipant } from '../types';

interface UseQRCodeResult {
  isGenerating: boolean;
  error: string | null;
  downloadQRCode: (dataURL: string, voucherCode: string, participantName: string) => void;
  shareQRCode: (dataURL: string, voucherCode: string, participantName: string, hotelName: string) => Promise<void>;
  downloadAllQRCodes: (trip: Trip) => Promise<void>;
  copyVoucherCode: (voucherCode: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Hook para gerenciar operações com QR codes
 */
export const useQRCode = (): UseQRCodeResult => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const downloadQRCode = useCallback((dataURL: string, voucherCode: string, participantName: string) => {
    try {
      QRCodeService.downloadQRCode(dataURL, voucherCode, participantName);
      setError(null);
    } catch (err) {
      setError('Erro ao fazer download do QR Code');
      console.error('Error downloading QR code:', err);
    }
  }, []);

  const shareQRCode = useCallback(async (
    dataURL: string, 
    voucherCode: string, 
    participantName: string, 
    hotelName: string
  ) => {
    try {
      setError(null);
      const result = await QRCodeService.shareQRCode(dataURL, voucherCode, participantName, hotelName);
      
      if (!result.success) {
        setError(result.message || 'Erro ao compartilhar QR Code');
      } else if (result.method === 'clipboard' && result.message) {
        // Mostrar feedback de sucesso para clipboard
        // Em uma implementação real, você pode usar um toast aqui
        console.log(result.message);
      }
    } catch (err) {
      setError('Erro ao compartilhar QR Code');
      console.error('Error sharing QR code:', err);
    }
  }, []);

  const downloadAllQRCodes = useCallback(async (trip: Trip) => {
    try {
      setIsGenerating(true);
      setError(null);
      
      await PDFService.generateTripQRCodesPDF(trip);
      
      console.log(`PDF com QR codes gerado para a viagem ${trip.hotel_name}`);
    } catch (err) {
      setError('Erro ao gerar PDF com QR Codes');
      console.error('Error generating PDF:', err);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const copyVoucherCode = useCallback(async (voucherCode: string) => {
    try {
      setError(null);
      const success = await FileService.copyToClipboard(voucherCode);
      
      if (!success) {
        setError('Erro ao copiar código do voucher');
      } else {
        console.log('Código do voucher copiado para a área de transferência');
      }
    } catch (err) {
      setError('Erro ao copiar código do voucher');
      console.error('Error copying voucher code:', err);
    }
  }, []);

  return {
    isGenerating,
    error,
    downloadQRCode,
    shareQRCode,
    downloadAllQRCodes,
    copyVoucherCode,
    clearError
  };
};
